<template>
  <app-page class="ttp-page" fill lean>
    <!-- Aufbau wie Benutzer und Geräte (Kundenwunsch 13.10.2026: „bitte einmal die bearbeitung der ticketarten
         grundlegend überarbeiten und an unser neues design anpassen! das gefällt mir so gar nicht!“): leichter Titel,
         rechts Aktualisieren und Ticketart anlegen (nur bei Schreibrecht der Tarifzone), links Tarifzone und Suche;
         Tabelle über die ganze Höhe, ein Klick öffnet rechts den Detailbereich zum Bearbeiten -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button v-if="canWrite" icon="plus" @click="addTicketType">Ticketart anlegen</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button v-if="canWrite" icon="plus" label="Ticketart anlegen" @click="addTicketType" />
      </template>
    </template>
    <template #toolbar>
      <app-select
        :value="zoneId"
        :items="zoneItems"
        prefix="Tarifzone"
        placeholder="Tarifzone wählen"
        :menu-width="280"
        @input="onZoneInput"
      />
      <app-search v-model="searchText" placeholder="In Ticketarten suchen" @input="onSearchInput" />
    </template>

    <div class="ttp-gridcol">
      <div class="app-grid-fill ttp-grid" :class="{ 'ttp-grid--auto': handy }">
        <dx-data-grid
          :ref="gridRefName"
          :height="gridHeight"
          :columns="columns"
          :data-source="dataSource"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :focused-row-enabled="true"
          :no-data-text="noDataText"
          @toolbar-preparing="onToolbarPreparing"
          @focused-row-changing="onFocusedRowChanging"
          @focused-row-changed="onFocusedRowChanged"
          @content-ready="onContentReady"
        >
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
          <dx-search-panel :visible="false" />
          <dx-selection mode="none" />
          <template #nameZelle="{ data }">
            <span class="ttp-name">
              <span class="ttp-name__dot" :style="dotStyle(data.data.button_color)" aria-hidden="true"></span>
              <span class="ttp-name__text">{{ data.data.name || "–" }}</span>
            </span>
          </template>
          <template #zaehlungZelle="{ data }">
            <span v-if="isOne(data.data.is_counting_relevant)" class="ttp-yes">
              <app-icon name="check" :size="14" class="ttp-yes__icon" />Ja
            </span>
            <span v-else class="ttp-muted"><span aria-hidden="true">–</span><span class="app-sr-only">Nein</span></span>
          </template>
          <template #mutedZelle="{ data }">
            <span class="ttp-muted">{{ data.text || "–" }}</span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
          <template v-if="zone && !canWrite">
            <span class="app-sep" aria-hidden="true">·</span>
            <span class="ttp-readonly"><app-icon name="lock" :size="12" />nur lesbar</span>
          </template>
        </template>
      </app-grid-footer>

      <app-dialog :value="discardDialog" title="Änderungen verwerfen?" @input="onDiscardDialogInput">
        <p class="ttp-dialog__text">Die ungespeicherten Änderungen gehen verloren.</p>
        <template #actions>
          <app-button @click="keepEditing">Weiter bearbeiten</app-button>
          <app-button kind="danger" @click="confirmDiscard">Verwerfen</app-button>
        </template>
      </app-dialog>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!editMode"
        wide
        icon="ticket"
        :title="paneTitle"
        close-label="Ticketart schließen"
        :dirty="dirty"
        @close="onDetailClose"
        @layout-change="onLayoutChange"
      >
        <ticket-type-editor
          v-if="editMode"
          :value="draft"
          :mode="editMode"
          :readonly="!canWrite"
          :errors="errors"
          :zone-name="zone ? zone.name : ''"
          @field="onField"
        />
        <template v-if="canWrite" #footer>
          <span class="app-sr-only" role="status">{{ saveError || changeText }}</span>
          <span
            v-if="saveError || dirty"
            class="ttp-foot__text"
            :class="{ 'ttp-foot__text--error': !!saveError }"
            aria-hidden="true"
          >
            <app-icon v-if="saveError" name="alert" :size="14" />{{ saveError || changeText }}
          </span>
          <app-button :disabled="!dirty || saving" @click="revertChanges">Verwerfen</app-button>
          <app-button kind="primary" icon="check" :disabled="!dirty" :loading="saving" @click="saveChanges">
            Speichern
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import { DxDataGrid, DxPaging, DxPager, DxSearchPanel, DxSelection } from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import DataGridMixin from "@/mixins/DataGridMixin";
import TicketTypeEditor from "@/components/masterdata/TicketTypeEditor.vue";
import { kindText } from "@/components/masterdata/ticketTypeOptions";
import { formatNumber, plural } from "@/utils/format";

// Felder des Detailbereichs (wie bisher das DevExtreme-Formular); Zahlen gehen als ganze Zahl an die Schnittstelle
// (bconn-tickettype-put/post unterscheiden longValue und stringValue am Typ), Zählung als 1/0
const NUMBER_FIELDS = [
  "ticket_type_id", "order_nr", "tariff_price_grade", "additional_price", "min_passenger", "max_passenger",
  "valid_from", "valid_to", "passnr_length", "type", "seats", "passenger_name_length",
];
const TEXT_FIELDS = ["name", "abbreviation", "toggle_text", "button_color", "notusedby", "print_definition"];
const EDIT_FIELDS = NUMBER_FIELDS.filter((k) => k !== "ticket_type_id").concat(TEXT_FIELDS, ["is_counting_relevant"]);
const CREATE_FIELDS = NUMBER_FIELDS.concat(TEXT_FIELDS, ["is_counting_relevant"]);
// in der Datenbank NOT NULL ohne Vorgabe: beim Anlegen Pflicht, beim Bearbeiten nicht leeren
const REQUIRED = ["name", "abbreviation", "tariff_price_grade", "additional_price", "min_passenger", "max_passenger",
  "valid_from", "valid_to", "passnr_length", "type"];
const ZONE_KEY = "ticketartenTarifzone";

function isOne(value) {
  return value === 1 || value === "1" || value === true;
}

function textValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

function sameValue(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return !!a === !!b;
  }
  return textValue(a) === textValue(b);
}

// Druckdefinition: die Schnittstelle speichert Zeilenumbrüche als „@“ (wie bisher)
function fromApiPrint(value) {
  return value ? String(value).replace(/@/g, "\n") : "";
}
function toApiPrint(value) {
  return String(value || "").replace(/\r?\n/g, "@");
}

function draftFrom(row) {
  const draft = {};
  NUMBER_FIELDS.forEach((k) => {
    draft[k] = textValue(row[k]);
  });
  TEXT_FIELDS.forEach((k) => {
    draft[k] = k === "print_definition" ? fromApiPrint(row[k]) : textValue(row[k]);
  });
  draft.is_counting_relevant = isOne(row.is_counting_relevant);
  return draft;
}

export default {
  name: "TicketType",
  components: { DxDataGrid, DxPaging, DxPager, DxSearchPanel, DxSelection, TicketTypeEditor },
  mixins: [DataGridMixin],
  data() {
    return {
      gridRefName: "ticketTypeGrid",
      gridAttr: { class: "app-grid--detail" },
      searchText: "",
      zones: [],
      zoneId: null,
      summary: { count: 0, loaded: 0 },
      // Detailbereich: null | 'edit' | 'create'
      editMode: null,
      editRow: null,
      draft: {},
      original: {},
      errors: {},
      saving: false,
      saveError: "",
      discardDialog: false,
      // Bildschirm: Nr., Name (mit Buttonfarbe), Buttontext, ID, Typ, Preisstufe, Fahrgäste, Zählung
      columns: [
        { dataField: "order_nr", caption: "Nr.", width: 84, alignment: "left", sortOrder: "asc", sortIndex: 0, cellTemplate: "mutedZelle" },
        { dataField: "name", caption: "Name", minWidth: 200, cellTemplate: "nameZelle" },
        { dataField: "abbreviation", caption: "Buttontext", width: 150 },
        { dataField: "ticket_type_id", caption: "ID", width: 72, alignment: "left", sortOrder: "asc", sortIndex: 1 },
        { name: "kind", caption: "Typ", width: 180, calculateCellValue: (row) => kindText(row.type) },
        { dataField: "tariff_price_grade", caption: "Preisstufe", width: 104, alignment: "left" },
        {
          name: "passengers",
          caption: "Fahrgäste",
          width: 104,
          calculateCellValue: (row) =>
            row.min_passenger === row.max_passenger ? textValue(row.min_passenger) : `${textValue(row.min_passenger)}–${textValue(row.max_passenger)}`,
        },
        { dataField: "is_counting_relevant", caption: "Zählung", width: 96, cellTemplate: "zaehlungZelle" },
      ],
      dataSource: new CustomStore({
        key: ["tariff_zone_id", "ticket_type_id"],
        load: async () => {
          if (this.zoneId === null) {
            this._rows = [];
            return [];
          }
          const data = (await axios.get(`${this.$store.state.baseUrl}tickettype?tariff_zone_id=${this.zoneId}`)).data;
          this._rows = Array.isArray(data) ? data : [];
          return this._rows;
        },
      }),
    };
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    zone() {
      return this.zones.find((z) => z.tariff_zone_id === this.zoneId) || null;
    },
    zoneItems() {
      return this.zones.map((z) => ({ value: z.tariff_zone_id, text: `${z.name} (${z.tariff_zone_id})` }));
    },
    canWrite() {
      return !!this.zone && this.zone.permission === "WRITE";
    },
    noDataText() {
      return this.zoneId === null ? "Bitte eine Tarifzone wählen" : "Keine Ticketarten in dieser Tarifzone";
    },
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Ticketart", "Ticketarten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Ticketart", "Ticketarten");
    },
    paneTitle() {
      if (this.editMode === "create") {
        return "Ticketart anlegen";
      }
      return this.canWrite ? "Ticketart bearbeiten" : "Ticketart";
    },
    changedFields() {
      if (!this.editMode) {
        return [];
      }
      const fields = this.editMode === "create" ? CREATE_FIELDS : EDIT_FIELDS;
      return fields.filter((key) => !sameValue(this.draft[key], this.original[key]));
    },
    dirty() {
      return this.changedFields.length > 0;
    },
    changeText() {
      const n = this.changedFields.length;
      if (!n) {
        return "Keine Änderungen";
      }
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
  },
  created() {
    this._rows = [];
    this.pendingDiscard = null;
    this.pendingKeep = null;
  },
  mounted() {
    this.loadZones();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty || this.saving || !this.$store.state.isLoggedIn) {
      next();
      return;
    }
    this.askDiscard(() => next(), () => next(false));
  },
  methods: {
    isOne,
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance ? ref.instance : null;
    },
    dotStyle(color) {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(color || "").trim());
      return m ? { background: "#" + m[1] } : null;
    },
    // ---------------------------------------------------------------- Tarifzonen
    async loadZones() {
      try {
        const data = (await axios.get(`${this.$store.state.baseUrl}tariffzone`)).data;
        this.zones = Array.isArray(data) ? data : [];
      } catch (e) {
        this.zones = [];
      }
      let saved = null;
      try {
        saved = Number(window.localStorage.getItem(ZONE_KEY));
      } catch (e) {
        saved = null;
      }
      const first = this.zones.find((z) => z.tariff_zone_id === saved) || this.zones[0];
      this.zoneId = first ? first.tariff_zone_id : null;
      this.refreshDataGrid();
    },
    onZoneInput(value) {
      if (value === this.zoneId) {
        return;
      }
      const apply = () => {
        this.closeDetail();
        this.zoneId = value;
        try {
          window.localStorage.setItem(ZONE_KEY, String(value));
        } catch (e) {
          // ohne Speicher gilt die Wahl bis zum Neuladen
        }
        this.refreshDataGrid();
      };
      if (this.dirty) {
        this.askDiscard(apply);
        return;
      }
      apply();
    },
    // ---------------------------------------------------------------- Grid
    refreshDataGrid() {
      const grid = this.grid();
      if (grid) {
        grid.refresh();
      }
    },
    onSearchInput() {
      const grid = this.grid();
      if (grid) {
        grid.searchByText(this.searchText || "");
      }
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.summary = { count, loaded: Math.max((this._rows || []).length, count) };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      this.syncEditRow();
    },
    // ---------------------------------------------------------------- Auswahl und Detailbereich
    onFocusedRowChanging(e) {
      if (this.saving) {
        e.cancel = true;
        return;
      }
      if (!this.dirty || e.newRowIndex === e.prevRowIndex) {
        return;
      }
      e.cancel = true;
      const index = e.newRowIndex;
      this.askDiscard(() => {
        const grid = this.grid();
        if (grid) {
          grid.option("focusedRowIndex", index);
        }
      });
    },
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.editMode === "edit" && this.editRow && String(this.editRow.ticket_type_id) === String(row.data.ticket_type_id)) {
        return;
      }
      this.openEdit(row.data);
    },
    openEdit(row) {
      this.editRow = Object.freeze(Object.assign({}, row));
      this.draft = draftFrom(row);
      this.original = Object.assign({}, this.draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "edit";
    },
    syncEditRow() {
      if (this.editMode !== "edit" || !this.editRow || this.dirty || this.saving) {
        return;
      }
      const fresh = (this._rows || []).find((r) => String(r.ticket_type_id) === String(this.editRow.ticket_type_id));
      if (fresh && JSON.stringify(fresh) !== JSON.stringify(this.editRow)) {
        this.openEdit(fresh);
      }
    },
    nextNumber(key) {
      const values = (this._rows || []).map((r) => Number(r[key])).filter((n) => Number.isFinite(n));
      return values.length ? Math.max.apply(null, values) + 1 : 1;
    },
    // Anlegen: ID und Sortierung als nächste freie Nummer, übliche Vorgaben für die Pflichtfelder
    openCreate() {
      const draft = draftFrom({
        ticket_type_id: this.nextNumber("ticket_type_id"),
        order_nr: this.nextNumber("order_nr"),
        tariff_price_grade: 1,
        additional_price: 0,
        min_passenger: 1,
        max_passenger: 1,
        valid_from: 0,
        valid_to: 0,
        passnr_length: 0,
        type: 0,
        seats: 1,
        passenger_name_length: 0,
        is_counting_relevant: 1,
      });
      this.editRow = null;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "create";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    addTicketType() {
      if (this.saving || !this.canWrite) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.openCreate());
        return;
      }
      this.openCreate();
    },
    closeDetail() {
      this.editMode = null;
      this.editRow = null;
      this.draft = {};
      this.original = {};
      this.errors = {};
      this.saveError = "";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailClose() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.closeDetail());
        return;
      }
      this.closeDetail();
    },
    // offener Detailbereich: Buttontext, Fahrgäste und Zählung ausblenden (stehen im Detail)
    onLayoutChange(e) {
      const grid = this.grid();
      if (grid) {
        const open = !!(e && e.open);
        grid.beginUpdate();
        ["abbreviation", "passengers", "is_counting_relevant"].forEach((c) => grid.columnOption(c, "visible", !open));
        grid.endUpdate();
      }
      this.$nextTick(() => {
        const current = this.grid();
        if (current) {
          current.updateDimensions();
        }
      });
    },
    onField(key, value) {
      this.draft = Object.assign({}, this.draft, { [key]: typeof value === "boolean" ? value : textValue(value) });
      if (this.errors[key]) {
        const errors = Object.assign({}, this.errors);
        delete errors[key];
        this.errors = errors;
      }
      this.saveError = "";
    },
    revertChanges() {
      this.draft = Object.assign({}, this.original);
      this.errors = {};
      this.saveError = "";
    },
    validate(fields) {
      const errors = {};
      fields.forEach((key) => {
        const value = textValue(this.draft[key]).trim();
        const required = REQUIRED.indexOf(key) !== -1 || (key === "ticket_type_id" && this.editMode === "create");
        if (!value) {
          // optionale Zahlen dürfen leer bleiben, wenn sie es vorher waren
          if (required || (NUMBER_FIELDS.indexOf(key) !== -1 && textValue(this.original[key]).trim() !== "")) {
            errors[key] = "Pflichtfeld";
          }
          return;
        }
        if (NUMBER_FIELDS.indexOf(key) !== -1 && !/^\d+$/.test(value)) {
          errors[key] = "Bitte eine ganze Zahl ab 0 eingeben.";
        }
      });
      if (this.editMode === "create" && !errors.ticket_type_id) {
        const id = textValue(this.draft.ticket_type_id).trim();
        if ((this._rows || []).some((r) => String(r.ticket_type_id) === id)) {
          errors.ticket_type_id = "ID bereits vergeben.";
        }
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    apiValue(key) {
      const value = this.draft[key];
      if (key === "is_counting_relevant") {
        return value ? 1 : 0;
      }
      if (NUMBER_FIELDS.indexOf(key) !== -1) {
        return Number(textValue(value).trim());
      }
      if (key === "print_definition") {
        return toApiPrint(value);
      }
      return textValue(value);
    },
    // nur geänderte Felder (Bearbeiten) bzw. alle gefüllten Felder (Anlegen) über dieselbe Schnittstelle wie bisher
    async saveChanges() {
      if (!this.dirty || this.saving || !this.canWrite) {
        return;
      }
      const create = this.editMode === "create";
      const fields = create ? CREATE_FIELDS : this.changedFields;
      if (!this.validate(create ? CREATE_FIELDS : this.changedFields)) {
        return;
      }
      const body = {};
      fields.forEach((key) => {
        const empty = textValue(this.draft[key]).trim() === "";
        // leere Zahlen nie, leere Texte beim Anlegen nicht schicken (Vorgabe der Datenbank)
        if (empty && (NUMBER_FIELDS.indexOf(key) !== -1 || (create && TEXT_FIELDS.indexOf(key) !== -1))) {
          return;
        }
        body[key] = this.apiValue(key);
      });
      const zoneId = this.zoneId;
      const ticketTypeId = create ? body.ticket_type_id : this.editRow.ticket_type_id;
      this.saving = true;
      this.saveError = "";
      try {
        if (create) {
          await axios.post(`${this.$store.state.baseUrl}tickettype`, Object.assign({ tariff_zone_id: zoneId }, body));
        } else {
          await axios.put(
            `${this.$store.state.baseUrl}tickettype?tariff_zone_id=${zoneId}&ticket_type_id=${ticketTypeId}`,
            body
          );
        }
      } catch (err) {
        this.saving = false;
        this.saveError = create ? "Die Ticketart konnte nicht angelegt werden." : "Änderungen konnten nicht gespeichert werden.";
        return;
      }
      this.original = Object.assign({}, this.draft);
      const grid = this.grid();
      if (grid) {
        try {
          await grid.refresh();
        } catch (err) {
          // Ladefehler zeigt das Grid selbst
        }
      }
      this.saving = false;
      if (this._isDestroyed || this.dirty) {
        return;
      }
      const fresh = (this._rows || []).find((r) => String(r.ticket_type_id) === String(ticketTypeId));
      if (!fresh) {
        if (create) {
          this.closeDetail();
        }
        return;
      }
      this.openEdit(fresh);
      const current = this.grid();
      if (current) {
        const key = current.keyOf(fresh);
        if (current.getRowIndexByKey(key) >= 0) {
          current.option("focusedRowKey", key);
        }
      }
    },
    // ---------------------------------------------------------------- Rückfrage „Änderungen verwerfen?“
    askDiscard(onDiscard, onKeep) {
      const previousKeep = this.pendingKeep;
      this.pendingDiscard = onDiscard;
      this.pendingKeep = onKeep || null;
      if (previousKeep) {
        previousKeep();
      }
      this.discardDialog = true;
    },
    confirmDiscard() {
      const action = this.pendingDiscard;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      this.revertChanges();
      if (action) {
        action();
      }
    },
    keepEditing() {
      const action = this.pendingKeep;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      if (action) {
        action();
      }
    },
    onDiscardDialogInput(open) {
      if (!open) {
        this.keepEditing();
      }
    },
  },
};
</script>

<style scoped>
.ttp-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ttp-grid.ttp-grid--auto {
  flex: none;
}
.ttp-grid ::v-deep .dx-sort-index-icon {
  display: none;
}
.ttp-grid ::v-deep .ttp-name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
}
/* Buttonfarbe als Punkt vor dem Namen (ohne Farbe ein Ring) */
.ttp-grid ::v-deep .ttp-name__dot {
  flex: none;
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: 1px solid var(--edge);
  border-radius: 4px;
  background: transparent;
}
.ttp-grid ::v-deep .ttp-name__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: var(--ink);
}
.ttp-grid ::v-deep .ttp-muted {
  color: var(--ink-3);
}
.ttp-grid ::v-deep .ttp-yes {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ttp-grid ::v-deep .ttp-yes__icon {
  color: var(--green-dot);
}
.ttp-readonly {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ttp-foot__text {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -4px -16px 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--line-2);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.ttp-foot__text--error {
  color: var(--red);
}
p.ttp-dialog__text {
  margin: 0;
}
</style>
